import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './Blog.css';

const WP_API = 'https://cms.footyscience.com/wp-json/wp/v2';

function CommentSection({ postId }) {
  const [comments, setComments] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState(null); // 'submitting' | 'success' | 'error'

  useEffect(() => {
    fetch(`${WP_API}/comments?post=${postId}&status=approve&per_page=100`)
      .then(r => r.json())
      .then(data => setComments(Array.isArray(data) ? data.map(c => ({
        id: c.id,
        author_name: c.author_name,
        date: c.date.slice(0, 10),
        content: c.content.rendered.replace(/<[^>]+>/g, '').trim(),
      })) : []))
      .catch(() => {});
  }, [postId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const resp = await fetch(`${WP_API}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          post: postId,
          author_name: name,
          author_email: email,
          content: body,
        }),
      });
      if (!resp.ok) throw new Error('Failed');
      setStatus('success');
      setName('');
      setEmail('');
      setBody('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="blog-comments">
      <h3 className="blog-comments-title">Comments ({comments.length})</h3>

      {comments.length > 0 && (
        <div className="blog-comment-list">
          {comments.map(c => (
            <div key={c.id} className="blog-comment">
              <div className="blog-comment-meta">
                <span className="blog-comment-author">{c.author_name}</span>
                <span className="blog-comment-date">{c.date}</span>
              </div>
              <p className="blog-comment-body">{c.content}</p>
            </div>
          ))}
        </div>
      )}

      <form className="blog-comment-form" onSubmit={handleSubmit}>
        <h4 className="blog-comment-form-title">Leave a comment</h4>
        <div className="blog-comment-form-row">
          <input
            className="blog-comment-input"
            type="text"
            placeholder="Name *"
            value={name}
            onChange={e => setName(e.target.value)}
            required
          />
          <input
            className="blog-comment-input"
            type="email"
            placeholder="Email * (not published)"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
        </div>
        <textarea
          className="blog-comment-textarea"
          placeholder="Your comment..."
          value={body}
          onChange={e => setBody(e.target.value)}
          rows={4}
          required
        />
        <div className="blog-comment-form-footer">
          <button
            className="blog-comment-submit"
            type="submit"
            disabled={status === 'submitting'}
          >
            {status === 'submitting' ? 'Submitting...' : 'Submit'}
          </button>
          {status === 'success' && (
            <span className="blog-comment-feedback success">
              Thanks! Your comment is awaiting moderation.
            </span>
          )}
          {status === 'error' && (
            <span className="blog-comment-feedback error">
              Something went wrong. Please try again.
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

function Blog() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTag, setActiveTag] = useState('All');

  useEffect(() => {
    Promise.all([
      fetch(`${WP_API}/posts?per_page=20&status=publish&_embed=1`).then(r => r.json()),
      fetch(`${WP_API}/tags?per_page=100`).then(r => r.json()),
    ])
      .then(([wpPosts, wpTags]) => {
        const tagMap = Object.fromEntries(wpTags.map(t => [t.id, t.name]));
        const posts = wpPosts.map(wp => ({
          id: wp.id,
          slug: wp.slug,
          title: wp.title.rendered,
          date: wp.date.slice(0, 10),
          tags: (wp.tags || []).map(id => tagMap[id]).filter(Boolean),
          excerpt: wp.excerpt.rendered.replace(/<[^>]+>/g, '').trim(),
          content_html: wp.content.rendered,
        }));
        setPosts(posts);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <div className="blog-container"><div className="loading">Loading posts...</div></div>;
  }

  if (error || !posts) {
    return (
      <div className="blog-container">
        <p className="blog-empty">No posts available yet. Check back soon!</p>
      </div>
    );
  }

  const allTags = ['All', ...Array.from(new Set(posts.flatMap(p => p.tags)))];
  const filtered = activeTag === 'All' ? posts : posts.filter(p => p.tags.includes(activeTag));

  const togglePost = (postSlug) => {
    if (slug === postSlug) {
      navigate('/blog');
    } else {
      navigate(`/blog/${postSlug}`);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="blog-container">
      <div className="blog-header">
        <h1>Blog</h1>
        <p className="subtitle">Weekly tips, round previews and model insights</p>
      </div>

      <div className="blog-tag-filter">
        {allTags.map(tag => (
          <button
            key={tag}
            className={`blog-tag-btn ${activeTag === tag ? 'active' : ''}`}
            onClick={() => setActiveTag(tag)}
          >
            {tag}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="blog-empty">No posts with tag "{activeTag}".</p>
      ) : (
        <div className="blog-post-list">
          {filtered.map(post => (
            <div key={post.slug} className="blog-post-item">
              <div
                className="blog-post-header"
                onClick={() => togglePost(post.slug)}
              >
                <div className="blog-post-title-row">
                  <h2 className="blog-post-title">{post.title}</h2>
                  <span className="blog-expand-icon">
                    {slug === post.slug ? '▲' : '▼'}
                  </span>
                </div>
                <div className="blog-post-meta">
                  <span className="blog-post-date">{formatDate(post.date)}</span>
                  <div className="blog-post-tags">
                    {post.tags.map(tag => (
                      <span key={tag} className="blog-tag-pill">{tag}</span>
                    ))}
                  </div>
                </div>
                {slug !== post.slug && (
                  <p className="blog-post-excerpt">{post.excerpt}</p>
                )}
              </div>

              {slug === post.slug && (
                <>
                  <div
                    className="blog-post-body"
                    dangerouslySetInnerHTML={{ __html: post.content_html }}
                  />
                  <CommentSection postId={post.id} />
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Blog;
